import { useQuery } from "@tanstack/react-query";
import { useTags } from "@/hooks/useTags";
import { 
  Brain, BookOpen, MessageSquare, Sparkles, Activity as ActivityIcon, 
  ChevronRight, Hash, TrendingUp, FileText, Coins
} from "lucide-react";
import { StatCard } from "@/components/StatCard";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { formatDistanceToNow, format, subDays, startOfDay } from "date-fns";
import { 
  ChartContainer, 
  ChartTooltip, 
  ChartTooltipContent,
  type ChartConfig
} from "@/components/ui/chart";
import { 
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, 
  PieChart, Pie, Cell, Area, AreaChart
} from "recharts";
import type { Tag, Topic, MastermindDocument } from "@shared/schema";
import { creditsQueryFn } from "@/services/creditsService";

type ActivityLog = {
  id: string;
  action: string;
  entityType: string;
  entityId: string | null;
  entityTitle: string | null;
  createdAt: string;
};

type Source = {
  id: string;
  title: string;
  createdAt?: string;
};

type ChatThread = {
  id: string;
  title: string | null;
  createdAt?: string;
};

type MastermindSpace = {
  id: string;
  name: string;
  createdAt?: string;
};

type TagWithUsage = Tag & {
  usageCount?: number;
};

type CreditInfo = {
  balance: string;
  totalEarned: string;
  totalSpent: string;
  nextResetAt: string;
};

export default function Stats() {
  const { data: sources = [] } = useQuery<Source[]>({
    queryKey: ["/api/sources"],
  });

  const { data: chatThreads = [] } = useQuery<ChatThread[]>({
    queryKey: ["/api/chat-threads"],
  });

  const { data: spaces = [] } = useQuery<MastermindSpace[]>({
    queryKey: ["/api/spaces"],
  });

  const { data: activityLogs = [] } = useQuery<ActivityLog[]>({
    queryKey: ["/api/activity-logs"],
  });

  const { data: allTags = [] } = useTags();

  const { data: topics = [] } = useQuery<Topic[]>({
    queryKey: ["/api/topics"],
  });

  const { data: documents = [] } = useQuery<MastermindDocument[]>({
    queryKey: ["/api/mastermind-documents"],
  });

  const { data: credits } = useQuery<CreditInfo>({
    queryKey: ["/api/credits"],
    queryFn: creditsQueryFn,
  });

  const stats = [
    { 
      title: "Chat Threads", 
      value: chatThreads.length.toString(), 
      icon: MessageSquare, 
      trend: { value: `${chatThreads.filter(t => {
        const created = new Date(t.createdAt || '');
        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);
        return created > weekAgo;
      }).length} this week`, positive: true } 
    },
    { 
      title: "Reference Sources", 
      value: sources.length.toString(), 
      icon: BookOpen 
    },
    { 
      title: "Mastermind Spaces", 
      value: spaces.length.toString(), 
      icon: Sparkles 
    },
    { 
      title: "Documents", 
      value: documents.length.toString(), 
      icon: FileText 
    },
  ];

  const chartConfig: ChartConfig = {
    value: {
      label: "Count",
      color: "hsl(var(--primary))",
    },
    spaces: {
      label: "Spaces",
      color: "hsl(var(--chart-1))",
    },
    threads: {
      label: "Threads",
      color: "hsl(var(--chart-2))",
    },
    sources: {
      label: "Sources",
      color: "hsl(var(--chart-3))",
    },
    documents: {
      label: "Documents",
      color: "hsl(var(--chart-4))",
    },
  };

  const generateGrowthData = () => {
    const last7Days = Array.from({ length: 7 }, (_, i) => {
      const date = startOfDay(subDays(new Date(), 6 - i));
      return {
        date: format(date, "EEE"),
        fullDate: date,
        spaces: 0,
        threads: 0,
        sources: 0,
        documents: 0,
      };
    });

    spaces.forEach(s => {
      if (s.createdAt) {
        const created = startOfDay(new Date(s.createdAt));
        const idx = last7Days.findIndex(d => d.fullDate.getTime() === created.getTime());
        if (idx !== -1) last7Days[idx].spaces++;
      }
    });

    chatThreads.forEach(t => {
      if (t.createdAt) {
        const created = startOfDay(new Date(t.createdAt));
        const idx = last7Days.findIndex(d => d.fullDate.getTime() === created.getTime());
        if (idx !== -1) last7Days[idx].threads++;
      }
    });

    sources.forEach(s => {
      if (s.createdAt) {
        const created = startOfDay(new Date(s.createdAt));
        const idx = last7Days.findIndex(d => d.fullDate.getTime() === created.getTime());
        if (idx !== -1) last7Days[idx].sources++;
      }
    });

    documents.forEach(d => {
      if (d.createdAt) {
        const created = startOfDay(new Date(d.createdAt as string));
        const idx = last7Days.findIndex(day => day.fullDate.getTime() === created.getTime());
        if (idx !== -1) last7Days[idx].documents++;
      }
    });

    return last7Days;
  };

  const growthData = generateGrowthData();

  const topTags = [...allTags]
    .filter(t => (t.usageCount || 0) > 0)
    .sort((a, b) => (b.usageCount || 0) - (a.usageCount || 0))
    .slice(0, 5);

  const pieColors = [
    "hsl(var(--chart-1))",
    "hsl(var(--chart-2))",
    "hsl(var(--chart-3))",
    "hsl(var(--chart-4))",
    "hsl(var(--chart-5))",
  ];

  const getActionIcon = (action: string) => {
    switch (action) {
      case "chat_created":
        return <MessageSquare className="h-4 w-4" />;
      case "source_added":
        return <BookOpen className="h-4 w-4" />;
      case "space_created":
      case "space_updated":
        return <Sparkles className="h-4 w-4" />;
      default:
        return <Brain className="h-4 w-4" />;
    }
  };

  const getActionText = (log: ActivityLog) => {
    switch (log.action) {
      case "chat_created":
        return "Created chat thread";
      case "source_added":
        return "Added source";
      case "space_created":
        return "Created space";
      case "space_updated":
        return "Updated space";
      case "knowledge_item_created":
        return "Created knowledge item";
      default:
        return log.action.replace(/_/g, " ");
    }
  };

  const getJumpUrl = (log: ActivityLog) => {
    switch (log.entityType) {
      case "chat":
        return "/";
      case "source":
        return "/library?filter=references";
      case "space":
        return `/spaces/${log.entityId}`;
      default:
        return "#";
    }
  };

  const groupLogsByDate = (logs: ActivityLog[]) => {
    const groups: { [key: string]: ActivityLog[] } = {};
    
    logs.forEach(log => {
      const date = new Date(log.createdAt);
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);

      let key;
      if (date.toDateString() === today.toDateString()) {
        key = "Today";
      } else if (date.toDateString() === yesterday.toDateString()) {
        key = "Yesterday";
      } else {
        key = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      }

      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(log);
    });

    return groups;
  };

  const groupedLogs = groupLogsByDate(activityLogs);

  const creditBalance = parseInt(credits?.balance || "0");
  const totalSpent = parseInt(credits?.totalSpent || "0");

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold mb-2" data-testid="text-page-title">Knowledge Analytics</h1>
        <p className="text-muted-foreground">Track your knowledge growth, usage patterns, and insights.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat) => (
          <StatCard key={stat.title} {...stat} />
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Knowledge Growth
            </CardTitle>
            <CardDescription>
              Content created over the last 7 days
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ChartContainer config={chartConfig} className="h-[250px]">
              <AreaChart data={growthData}>
                <XAxis 
                  dataKey="date" 
                  tickLine={false} 
                  axisLine={false}
                  fontSize={12}
                />
                <YAxis 
                  tickLine={false} 
                  axisLine={false} 
                  fontSize={12}
                  allowDecimals={false}
                />
                <ChartTooltip content={<ChartTooltipContent />} />
                <Area
                  type="monotone"
                  dataKey="spaces"
                  stackId="1"
                  stroke="hsl(var(--chart-1))"
                  fill="hsl(var(--chart-1))"
                  fillOpacity={0.3}
                />
                <Area
                  type="monotone"
                  dataKey="threads"
                  stackId="1"
                  stroke="hsl(var(--chart-2))"
                  fill="hsl(var(--chart-2))"
                  fillOpacity={0.3}
                />
                <Area
                  type="monotone"
                  dataKey="sources"
                  stackId="1"
                  stroke="hsl(var(--chart-3))"
                  fill="hsl(var(--chart-3))"
                  fillOpacity={0.3}
                />
                <Area
                  type="monotone"
                  dataKey="documents"
                  stackId="1"
                  stroke="hsl(var(--chart-4))"
                  fill="hsl(var(--chart-4))"
                  fillOpacity={0.3}
                />
              </AreaChart>
            </ChartContainer>
            <div className="flex flex-wrap justify-center gap-4 mt-4">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: "hsl(var(--chart-1))" }} />
                <span className="text-xs text-muted-foreground">Spaces</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: "hsl(var(--chart-2))" }} />
                <span className="text-xs text-muted-foreground">Threads</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: "hsl(var(--chart-3))" }} />
                <span className="text-xs text-muted-foreground">Sources</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: "hsl(var(--chart-4))" }} />
                <span className="text-xs text-muted-foreground">Documents</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Hash className="h-5 w-5" />
              Top Tags
            </CardTitle>
            <CardDescription>
              Most frequently used tags across your knowledge base
            </CardDescription>
          </CardHeader>
          <CardContent>
            {topTags.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Hash className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No tags used yet. Start tagging your content!</p>
              </div>
            ) : (
              <div className="space-y-4">
                {topTags.map((tag, i) => {
                  const maxCount = topTags[0]?.usageCount || 1;
                  const percentage = ((tag.usageCount || 0) / maxCount) * 100;
                  return (
                    <div key={tag.id} className="space-y-1">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Badge 
                            variant="secondary" 
                            style={tag.color ? { backgroundColor: tag.color + "20", borderColor: tag.color } : undefined}
                            data-testid={`tag-${tag.id}`}
                          >
                            <Hash className="h-3 w-3 mr-1" />
                            {tag.name}
                          </Badge>
                        </div>
                        <span className="text-sm text-muted-foreground">{tag.usageCount} uses</span>
                      </div>
                      <Progress value={percentage} className="h-2" />
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Coins className="h-5 w-5" />
              Credit Usage
            </CardTitle>
            <CardDescription>
              Your AI credit balance and usage
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="text-center">
                <p className="text-4xl font-bold text-primary" data-testid="text-credit-balance">
                  {creditBalance.toLocaleString()}
                </p>
                <p className="text-sm text-muted-foreground">credits remaining</p>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Total Spent</span>
                  <span className="font-medium">{totalSpent.toLocaleString()} credits</span>
                </div>
                {credits?.nextResetAt && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Next Reset</span>
                    <span className="font-medium">
                      {format(new Date(credits.nextResetAt), "MMM d, yyyy")}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Brain className="h-5 w-5" />
              Knowledge Summary
            </CardTitle>
            <CardDescription>
              Overview of your knowledge base composition
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 bg-muted/50 rounded-lg text-center">
                <p className="text-2xl font-bold">{spaces.length}</p>
                <p className="text-xs text-muted-foreground">Spaces</p>
              </div>
              <div className="p-4 bg-muted/50 rounded-lg text-center">
                <p className="text-2xl font-bold">{topics.length}</p>
                <p className="text-xs text-muted-foreground">Topics</p>
              </div>
              <div className="p-4 bg-muted/50 rounded-lg text-center">
                <p className="text-2xl font-bold">{documents.length}</p>
                <p className="text-xs text-muted-foreground">Documents</p>
              </div>
              <div className="p-4 bg-muted/50 rounded-lg text-center">
                <p className="text-2xl font-bold">{allTags.length}</p>
                <p className="text-xs text-muted-foreground">Tags</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Activity Timeline</CardTitle>
          <CardDescription>
            Chronological feed of all your actions across the platform
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[400px] pr-4">
            {activityLogs.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <ActivityIcon className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No activity yet. Start creating content to see your timeline!</p>
              </div>
            ) : (
              <div className="space-y-6">
                {Object.entries(groupedLogs).map(([date, logs]) => (
                  <div key={date} className="space-y-3">
                    <div className="flex items-center gap-2 sticky top-0 bg-background py-2 z-10">
                      <h3 className="text-sm font-semibold text-muted-foreground">{date}</h3>
                      <div className="flex-1 h-px bg-border"></div>
                    </div>
                    {logs.map((log) => (
                      <a
                        key={log.id}
                        href={getJumpUrl(log)}
                        className="flex items-center gap-4 p-3 rounded-lg hover-elevate active-elevate-2 group"
                        data-testid={`activity-${log.id}`}
                      >
                        <div className="p-2 bg-primary/10 rounded-lg shrink-0">
                          {getActionIcon(log.action)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium">{getActionText(log)}</p>
                          {log.entityTitle && (
                            <p className="text-sm text-muted-foreground truncate">
                              {log.entityTitle}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Badge variant="outline" className="text-xs">
                            {formatDistanceToNow(new Date(log.createdAt), { addSuffix: true })}
                          </Badge>
                          <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </a>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
}
